// The shared LOAD frame every Work surface renders inside. It turns the injected-projection lifecycle
// (loading → loaded | error) into one of four honest states so no surface fabricates data (P-SELF): a quiet
// "loading" placeholder, an honest ROLE-REFUSAL panel when the owning read forbids this account (a 403 —
// a PERMANENT boundary, never dressed as an outage), an honest "temporarily unavailable" panel for any other
// failure with NO internal detail (G-197), or the loaded body. It carries no domain logic — it renders
// whichever state the useProjection hook reports. Distinguishing a 403 keeps the copy HONEST (a member must
// never read "temporarily unavailable" for a permanent role boundary) — it is NOT a security control: the
// owning read is the boundary and the frame widens nothing (G-180).
//
// Keeping this here (not per surface) is what keeps each surface component small (file/function ceilings,
// G-085) and keeps the failure copy uniform (§8 degradation: fail honest and partial on rendering).

import type { ReactNode } from 'react';
import type { ProjectionState } from './useProjection.ts';
import { Panel } from './primitives.tsx';

/** Render one of the three honest projection states. `children` renders only when data is present. */
export function SurfaceFrame<T>({
  state,
  children,
}: {
  readonly state: ProjectionState<T>;
  readonly children: (data: T) => ReactNode;
}): React.JSX.Element {
  if (state.error) {
    // A 403 is a PERMANENT role/permission boundary, not an outage — say so honestly (never the
    // transient-outage copy). The frame names no role and reveals no internal detail (G-197).
    if (state.errorStatus === 403) {
      return (
        <Panel className="px-5 py-6">
          <p className="text-sm text-text-muted">You do not have access to this view.</p>
          <p className="mt-1 text-sm text-text-subtle">
            This view is limited to another role — your account cannot open it. Nothing is wrong,
            and nothing here is stale or made up.
          </p>
        </Panel>
      );
    }
    return (
      <Panel className="px-5 py-6">
        <p className="text-sm text-text-muted">This view is temporarily unavailable.</p>
        <p className="mt-1 text-sm text-text-subtle">
          The data could not be loaded right now. Nothing shown here is stale or made up.
        </p>
      </Panel>
    );
  }
  if (state.loading || state.data === null) {
    return (
      <div aria-busy="true" className="px-5 py-6 text-sm text-text-subtle">
        Loading…
      </div>
    );
  }
  return <>{children(state.data)}</>;
}
